import React, { useState, useRef, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import chatbotLogo from "../assets/chatbot-logo.png";
import API from "../services/api";
import "./Chatbot.css";

const FOUNDER_NAME = " Karthik Nakka  ";
const FOUNDER_LINKEDIN_URL = "https://www.linkedin.com/in/blackroth-blackroth";

const Chatbot = () => {
  const location = useLocation();
  const isOfferValidationPage =
    location.pathname === "/validate-and-accept-offer";
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: "Hiyaa! 👋 Welcome to BlackRoth! I'm Lexy, your AI assistant. How can I help you today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const complaintSubmittingRef = useRef(false);

  const [greeting, setGreeting] = useState(false);
  const [greetingStep, setGreetingStep] = useState(0);
  const [greetingFrame, setGreetingFrame] = useState(0);
  const [offerValidationStep, setOfferValidationStep] = useState(0);
  const [offerCandidateId, setOfferCandidateId] = useState("");

  const [ticketMode, setTicketMode] = useState(false);
  const [ticketStep, setTicketStep] = useState(0);
  const [ticketData, setTicketData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    description: "",
  });

  const resetChatbot = () => {
    setTicketMode(false);
    setTicketStep(0);
    setTicketData({
      name: "",
      email: "",
      phone: "",
      subject: "",
      description: "",
    });
    setOfferValidationStep(0);
    setOfferCandidateId("");

    setMessages([
      {
        role: "bot",
        text: "Hiyaa! 👋 Welcome to BlackRoth! I'm Lexy, your AI assistant. How can I help you today?",
      },
    ]);
  };

  const greetingMessages = useMemo(
    () => [
      "Hi! 👋",
      "Need help?",
      "I'm here to help you!",
      "Need info about BlackRoth? 🚀",
    ],
    []
  );

  const renderGreetingMessage = () => {
    const greetingText = greetingMessages[greetingStep];
    const brandStart = greetingText.indexOf("BlackRoth");

    if (brandStart === -1) {
      return greetingText;
    }

    const brandEnd = brandStart + "BlackRoth".length;

    return (
      <span className="chatbot-greeting-animated-text">
        {[...greetingText].map((char, index) => {
          const visibleFrame = greetingFrame - index;
          const classNames = ["chatbot-greeting-letter"];

          if (char === " ") {
            classNames.push("is-space");
          } else if (visibleFrame < 0) {
            classNames.push("is-hidden");
          } else if (visibleFrame === 0) {
            classNames.push("is-early");
          } else if (visibleFrame === 1) {
            classNames.push("is-dim");
          } else if (visibleFrame === 2) {
            classNames.push("is-bright");
          } else {
            classNames.push("is-final");
          }

          if (index >= brandStart && index < brandEnd) {
            classNames.push("is-brand");
          }

          return (
            <span key={`${char}-${index}`} className={classNames.join(" ")}>
              {char}
            </span>
          );
        })}
      </span>
    );
  };

  const renderBrandText = (text) => {
    const normalizedText = text.replace(/BlackRoth™/g, "BlackRoth");
    const parts = normalizedText.split("BlackRoth");

    return parts.map((part, index) => (
      <React.Fragment key={`message-text-${index}`}>
        {part}
        {index < parts.length - 1 && (
          <span className="chatbot-brand-text">BlackRoth</span>
        )}
      </React.Fragment>
    ));
  };

  const renderQuickReplyText = (text) => renderBrandText(text);

  const renderMessageText = (msg) => {
    if (!msg.founderLinkText || !msg.founderLinkUrl) {
      return renderBrandText(msg.text);
    }

    const founderParts = msg.text.split(msg.founderLinkText);

    return founderParts.flatMap((part, index) => {
      const nodes = [renderBrandText(part)];

      if (index < founderParts.length - 1) {
        nodes.push(
          <a
            key={`founder-link-${index}`}
            href={msg.founderLinkUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {msg.founderLinkText}
          </a>
        );
      }

      return nodes;
    });
  };

  const quickReplies = [
    "What services do you offer?",
    "Tell me about BlackRoth",
    "How to contact you?",
    "Careers & Jobs",
    "Our Team / About Us",
    "Partner with Us",
    "Raise a Ticket 🎫",
  ];

  const responses = {
    greet: `Hiyaa! 👋 Welcome to BlackRoth! I'm Lexy, your AI assistant. How can I help you today?`,
    services: `We offer the following services:\n\n• AI-Powered Fleet Management\n• Fleet Tracking & Real-Time Monitoring\n• Software Support & Maintenance\n• Fleet Analytics & Reporting\n• Custom Logistics Software`,
    about: `BlackRoth is a tech-driven company based , specializing in AI-Powered Integrated Fleet Management solutions.`,
    contact: `📍 12B, BLACKROTH, THE HEADQUARTERS, 6TH FLOOR, ORBIT, KNOWLEDGE CITY, RAIDURG, HYDERABAD, TELANGANA-500019\n✉ info@blackroth.in`,
    team: {
      text: `About BlackRoth:\n\nBlackRoth is a technology-focused organization committed to delivering reliable, scalable, and future-ready digital solutions.\n\nOur Mission:\nTo empower organizations with innovative tech.\n\nCo-Founders:\n• ${FOUNDER_NAME}\n\n`,
      founderLinkText: FOUNDER_NAME,
      founderLinkUrl: FOUNDER_LINKEDIN_URL,
    },
    partner: `🤝 Partner with BlackRoth!\n\nWe’re open to collaborations.\n\n👉 https://blackroth.in/contact`,
    default: `I'm not sure about that. You can ask me about services, careers, or contact 😊`,
  };

  const fetchCareers = async () => {
    try {
      const res = await API.get("/public/jobs");
      const data = res.data;
      const jobs = data.jobs || [];

      if (jobs.length === 0) {
        return `🚀 No openings right now.\n\n👉 https://blackroth.in/careers`;
      }

      const jobList = jobs
        .map((j) => `• ${j.title} (${j.location}) - ${j.employment_type}`)
        .join("\n");

      return `🚀 Current Openings:\n\n${jobList}\n\n👉 Apply: https://blackroth.in/careers`;
    } catch (err) {
      console.error(err);
      return `🚀 Check careers 👉 https://blackroth.in/careers`;
    }
  };

  const startTicketFlow = () => {
    setTicketMode(true);
    setTicketStep(1);
    setTicketData({
      name: "",
      email: "",
      phone: "",
      subject: "",
      description: "",
    });

    setMessages((prev) => [
      ...prev,
      {
        role: "bot",
        text: "Sure! Let's raise a ticket 🎫\n\nWhat is your name?\n\n(Please type your response in the input box below 👇)",
      },
    ]);
  };

  const handleTicketFlow = (text) => {
    if (ticketStep === 1) {
      setTicketData((p) => ({ ...p, name: text }));
      setTicketStep(2);
      setMessages((p) => [
        ...p,
        {
          role: "bot",
          text: "Enter your email:\n\n(Please type in the input box below 👇)",
        },
      ]);
    } else if (ticketStep === 2) {
      setTicketData((p) => ({ ...p, email: text }));
      setTicketStep(3);
      setMessages((p) => [
        ...p,
        {
          role: "bot",
          text: "Enter your phone number:\n\n(Please type in the input box below 👇)",
        },
      ]);
    } else if (ticketStep === 3) {
      setTicketData((p) => ({ ...p, phone: text }));
      setTicketStep(4);
      setMessages((p) => [
        ...p,
        {
          role: "bot",
          text: "Enter subject:\n\n(Please type in the input box below 👇)",
        },
      ]);
    } else if (ticketStep === 4) {
      setTicketData((p) => ({ ...p, subject: text }));
      setTicketStep(5);
      setMessages((p) => [
        ...p,
        {
          role: "bot",
          text: "Describe your issue:\n\n(Please type in the input box below 👇)",
        },
      ]);
    } else if (ticketStep === 5) {
      setTicketData((p) => ({ ...p, description: text }));
      setTicketStep(6);

      setMessages((p) => [
        ...p,
        {
          role: "bot",
          text: "Click below to submit your complaint 👇",
          showSubmit: true,
        },
      ]);
    }
  };

  const submitTicket = async () => {
    if (complaintSubmittingRef.current) return;

    try {
      complaintSubmittingRef.current = true;
      setLoading(true);
      await API.post("/public/complaint", ticketData);

      setMessages((prev) => [
        ...prev,
        { role: "bot", text: "✅ Complaint submitted successfully!" },
      ]);

      setTicketMode(false);
      setTicketStep(0);
    } catch (err) {
      console.error(err);
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text:
            "❌ Failed to submit complaint.You’ve hit the limit for ticket submissions.\n\nYou can try again by clicking 'Raise a Ticket 🎫'",
        },
      ]);

      setTicketMode(false);
      setTicketStep(0);
    } finally {
      complaintSubmittingRef.current = false;
      setLoading(false);
    }
  };

  const getReply = async (message) => {
    const msg = message.toLowerCase();

    if (msg.match(/hi|hello|hey|hiya|howdy/)) return responses.greet;
    if (msg.match(/service|offer|provide|do you/)) return responses.services;
    if (msg.match(/team|founder|who are|our team|about us/))
      return responses.team;
    if (msg.match(/about|company|what is blackroth/))
      return responses.about;
    if (msg.match(/contact|location|address|reach/))
      return responses.contact;
    if (msg.match(/career|job|hiring|apply|opening/))
      return await fetchCareers();
    if (msg.match(/partner|collaborate|partnership/))
      return responses.partner;

    return responses.default;
  };

  const sendMessage = async (text) => {
    const userText = text || input.trim();
    if (!userText || loading) return;

    if (userText.toLowerCase().includes("partner with us")) {
      setOpen(false);
      window.dispatchEvent(new Event("openGetStarted"));
      return;
    }

    setMessages((prev) => [...prev, { role: "user", text: userText }]);
    setInput("");

    if (offerValidationStep === 1) {
      setOfferCandidateId(userText.trim());
      setOfferValidationStep(2);
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text:
            "Enter your email:\n\n(Please type in the input box below 👇)",
        },
      ]);
      return;
    }

    if (offerValidationStep === 2) {
      const email = userText.trim();
      setOfferValidationStep(0);

      try {
        const response = await API.post("/public/offer/validate", {
          candidateId: offerCandidateId,
          email,
        });

        if (response?.data?.success) {
          setMessages((prev) => [
            ...prev,
            {
              role: "bot",
              text: "Your offerLetter got Validated Successfully",
            },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              role: "bot",
              text:
                "Your OfferLetter is Not validated\n\nSuspect to be a Fraud.\nRaise a fraud complaint ticket:",
              showFraudLink: true,
            },
          ]);
        }
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            role: "bot",
            text:
              "Your OfferLetter is Not validated\n\nSuspect to be a Fraud.\nRaise a fraud complaint ticket:",
            showFraudLink: true,
          },
        ]);
      } finally {
        setOfferCandidateId("");
      }

      return;
    }

    if (ticketMode) {
      if (userText.toLowerCase().includes("raise a ticket")) {
        startTicketFlow();
        return;
      }

      handleTicketFlow(userText);
      return;
    }

    if (userText.toLowerCase().includes("raise a ticket")) {
      startTicketFlow();
      return;
    }

    setLoading(true);

    try {
      const reply = await getReply(userText);
      const botMessage =
        typeof reply === "string"
          ? { role: "bot", text: reply }
          : { role: "bot", ...reply };

      setMessages((prev) => [...prev, botMessage]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: "Oops! Something went wrong 😅" },
      ]);
    }

    setLoading(false);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setGreeting(true);
      setGreetingStep(0);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!greeting || open) return;
    if (greetingStep >= greetingMessages.length - 1) return;

    const timer = setTimeout(() => {
      setGreetingStep((prev) => prev + 1);
    }, 2500);

    return () => clearTimeout(timer);
  }, [greeting, greetingMessages.length, greetingStep, open]);

  useEffect(() => {
    const greetingText = greetingMessages[greetingStep];

    setGreetingFrame(0);

    if (!greeting || open || !greetingText.includes("BlackRoth")) return;

    const timer = setInterval(() => {
      setGreetingFrame((prev) => {
        const finalFrame = [...greetingText].length + 18;
        return prev >= finalFrame ? 0 : prev + 1;
      });
    }, 80);

    return () => clearInterval(timer);
  }, [greeting, greetingMessages, greetingStep, open]);

  return (
    <>
      <style>
        {`
          .rover-chatbot-messages {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }

          .rover-chatbot-messages::-webkit-scrollbar {
            display: none;
            width: 0;
            height: 0;
          }
        `}
      </style>

      {greeting && !open && (
        <div className="chatbot-greeting" onClick={() => setOpen(true)}>
          {renderGreetingMessage()}
          <div className="chatbot-greeting-arrow" />
        </div>
      )}

      {!open && (
        <button className="chatbot-toggle-btn" onClick={() => setOpen(true)}>
          <img src={chatbotLogo} alt="chatbot" className="chatbot-logo" />
        </button>
      )}

      {open && (
        <>
          <div className="chatbot-container">
            <div className="chatbot-header">
              <img src={chatbotLogo} alt="Rover" className="chatbot-avatar" />
              <div>
                <div className="chatbot-title">
                  Lexy - Your AI Assistant
                </div>
                <div className="chatbot-status">
                  <span className="chatbot-status-dot" />
                </div>
              </div>
            </div>

            <div className="chatbot-messages">
              {messages.map((msg, i) => (
                <div key={i}>
                  <div className={`chatbot-message ${msg.role}`}>
                    {renderMessageText(msg)}
                    {msg.showFraudLink && (
                      <>
                        {" "}
                        <a href="/fraud-alert">Fraud Alert</a>
                      </>
                    )}
                  </div>

                  {msg.showSubmit && (
                    <button
                      className="chatbot-submit-btn"
                      onClick={submitTicket}
                      disabled={loading}
                    >
                      {loading ? "Submitting..." : "Submit Complaint"}
                    </button>
                  )}
                </div>
              ))}

              {loading && <div className="chatbot-typing">Typing...</div>}
              <div ref={messagesEndRef} />
            </div>

            <div className="chatbot-quick-replies">
              {quickReplies.map((q, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(q)}
                  className="chatbot-quick-btn"
                >
                  {renderQuickReplyText(q)}
                </button>
              ))}
              {!isOfferValidationPage && (
                <button
                  onClick={() => {
                    setMessages((prev) => [
                      ...prev,
                      { role: "user", text: "Validate OfferLetter" },
                      {
                        role: "bot",
                        text:
                          "lets check the validate offer letter\n\nWhat is your Candidate id ?\n\nPlease type your response in the input box below 👇",
                      },
                    ]);
                    setOfferValidationStep(1);
                    setOfferCandidateId("");
                  }}
                  className="chatbot-quick-btn"
                >
                  Validate OfferLetter
                </button>
              )}
            </div>

            <div className="chatbot-input-container">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Ask me anything..."
                className="chatbot-input"
              />
              <button
                onClick={() => sendMessage()}
                className="chatbot-send-btn"
              >
                ➤
              </button>
            </div>
          </div>

          <button
            className="chatbot-close-btn"
            onClick={() => {
              setOpen(false);
              resetChatbot();
            }}
          >
            ×
          </button>
        </>
      )}
    </>
  );
};

export default Chatbot;
